/**
 * Where a quoted literal sits in an expression — the grammar's most primitive
 * rule, and the one every other reader has to agree with: a scan that
 * disagrees about where a string starts treats its content as code. Read only
 * through `FORMULA_ALTERNATIVES`, which is how every reader gets it.
 */
const FORMULA_STRING_LITERAL = /'(?:\\.|[^'\\])*'|"(?:\\.|[^"\\])*"/

/**
 * What a piece of an expression is.
 *
 * The one list every surface that takes a formula apart reads: the editor maps
 * its parser's tags onto it, the catalogue's cards read it off the text, and
 * the formatter asks only which pieces are strings. Renaming a member is a
 * build error in all of them.
 */
export type FormulaToken =
  | 'keyword'
  | 'constant'
  | 'number'
  | 'string'
  | 'call'
  | 'name'
  | 'operator'
  | 'bracket'

/** The words the language owns, which a JavaScript parser reads as ordinary
 *  names. */
export const FORMULA_KEYWORDS = ['and', 'or', 'not', 'in', 'to'] as const

/** What reads as a value rather than something the expression can change. */
export const FORMULA_CONSTANTS = ['true', 'false', 'null'] as const

/**
 * Every operator the language writes, longest first so `<=` is read before
 * `<` and `!=` before `!`.
 *
 * The scanner splits a run of operator characters by this list, so `a*-2` is
 * `*` then `-` and not one piece: a surface that colours them cannot tell the
 * difference, and one that spaces them out has nothing else to go by.
 */
const FORMULA_OPERATORS = [
  '==',
  '!=',
  '<=',
  '>=',
  '+',
  '-',
  '*',
  '/',
  '^',
  '%',
  '<',
  '>',
  '?',
  ':',
  ',',
] as const

const WORD = new Set<string>(FORMULA_KEYWORDS)
const CONSTANT = new Set<string>(FORMULA_CONSTANTS)

/**
 * One pass over the grammar's surface, in the order the alternatives have to
 * be tried: a quoted string first, because anything inside it is content.
 *
 * Its string alternative is the evaluator's own pattern, so no scan can
 * disagree with the evaluator about where a string starts — the space in
 * `'Bad  EG'` is content, and a formatter that misses that spaces it out.
 */
/**
 * The alternatives the scan tries, in the order they have to be tried: a
 * quoted string first, because anything inside it is content.
 *
 * Each carries its source and the same source anchored and compiled, so a
 * reader that walks a whole expression and one that walks a line at a time —
 * CodeMirror's stream parser — share the compiled patterns rather than the
 * strings.
 */
export const FORMULA_ALTERNATIVES = [
  { kind: 'string', source: FORMULA_STRING_LITERAL.source },
  { kind: 'number', source: '\\d+(?:\\.\\d+)?' },
  { kind: 'word', source: '[A-Za-z_][A-Za-z0-9_]*' },
  { kind: 'bracket', source: '[()\\[\\]{}]' },
  { kind: 'operator', source: '[-+*/^%<>=!&|?:,]+' },
].map(alternative => ({
  ...alternative,
  at: new RegExp(`^(?:${alternative.source})`),
})) as readonly {
  kind: 'string' | 'number' | 'word' | 'bracket' | 'operator'
  source: string
  at: RegExp
}[]

const SCANNER = new RegExp(
  `${FORMULA_ALTERNATIVES.map(alt => `(${alt.source})`).join('|')}|\\s+`,
  'g',
)

/**
 * What a bare word is, given what follows it.
 *
 * Exported with the alternatives above so a reader that walks one line at a
 * time — CodeMirror's stream parser — classifies a word exactly as a reader
 * that walks a whole expression does.
 */
export function classifyWord(word: string, after: string): FormulaToken {
  if (CONSTANT.has(word)) return 'constant'
  if (WORD.has(word)) return 'keyword'
  // A name followed by `(` is being called; one standing alone is read.
  return after.trimStart().startsWith('(') ? 'call' : 'name'
}

export type FormulaPiece = { text: string; token: FormulaToken | null }

/**
 * Every piece of an expression in order, whitespace and anything the grammar
 * does not know included — those carry no token.
 *
 * A reader, not a parser: it is what a surface paints a formula with where
 * mounting the editor for every row would load CodeMirror to show four lines
 * of text. The editor itself still colours by the real parse, so the two agree
 * on what a name is without agreeing on how to find it.
 *
 * Unmerged, which is what a caller spacing an expression out needs: `tokenizeFormula`
 * joins neighbours of one token, and `a / *b` would come back as one operator.
 */
export function scanFormula(code: string): FormulaPiece[] {
  const out: FormulaPiece[] = []
  let read = 0

  for (const match of code.matchAll(SCANNER)) {
    if (match.index > read) {
      out.push({ text: code.slice(read, match.index), token: null })
    }
    read = match.index + match[0].length

    const [all, string, number, word, bracket, operator] = match
    if (string) out.push({ text: all, token: 'string' })
    else if (number) out.push({ text: all, token: 'number' })
    else if (word) {
      out.push({ text: all, token: classifyWord(word, code.slice(read)) })
    } else if (bracket) out.push({ text: all, token: 'bracket' })
    else if (operator) {
      // Split by the list above, so each operator is its own piece. A
      // character the list does not name is a piece of its own.
      let rest = all
      while (rest.length > 0) {
        const found = FORMULA_OPERATORS.find(op => rest.startsWith(op))
        if (found) {
          out.push({ text: found, token: 'operator' })
          rest = rest.slice(found.length)
        } else {
          out.push({ text: rest[0], token: 'operator' })
          rest = rest.slice(1)
        }
      }
    } else out.push({ text: all, token: null })
  }
  if (read < code.length) out.push({ text: code.slice(read), token: null })

  return out
}

/** An expression as the pieces a surface colours it in, neighbours of one
 *  token merged so a run of brackets is one span and not three. */
export function tokenizeFormula(code: string): FormulaPiece[] {
  const out: FormulaPiece[] = []
  for (const piece of scanFormula(code)) {
    const last = out.at(-1)
    if (last && last.token === piece.token) last.text += piece.text
    else out.push({ ...piece })
  }
  return out
}

/**
 * What an expression reads, and which of those it reads behind an `isset`.
 *
 * Off the one scan, so what counts as a reference is what the editor paints as
 * a name: a word inside a quoted literal is content, and a usage search that
 * counts it refuses to delete something nothing uses.
 *
 * `issetGuarded` is `isset(x)` and nothing looser — the guard the evaluator
 * reads as "the author handled this being unset", which `isset(x + y)` is not.
 */
/**
 * What one expression reads, kept for the life of the process.
 *
 * The answer depends on the code alone, and the same rule is asked once per
 * null variable on every evaluation — an availability sweep asks it hundreds
 * of times for the same handful of strings. Capped, because the formula editor
 * asks it for a fresh draft on every keystroke: past the cap the table is
 * dropped whole, which keeps the catalogue's own rules warm again within one
 * sweep.
 */
const REFERENCES_CACHE = new Map<string, FormulaReferences>()
const REFERENCES_CACHE_MAX = 500

export type FormulaReferences = {
  names: Set<string>
  issetGuarded: Set<string>
}

export function formulaReferences(code: string): FormulaReferences {
  const cached = REFERENCES_CACHE.get(code)
  if (cached) return cached
  const read = scanReferences(code)
  if (REFERENCES_CACHE.size >= REFERENCES_CACHE_MAX) REFERENCES_CACHE.clear()
  REFERENCES_CACHE.set(code, read)
  return read
}

function scanReferences(code: string): FormulaReferences {
  const names = new Set<string>()
  const issetGuarded = new Set<string>()
  const pieces = scanFormula(code).filter(piece => piece.token !== null)

  for (let at = 0; at < pieces.length; at++) {
    const piece = pieces[at]
    if (piece.token === 'name' || piece.token === 'call') names.add(piece.text)
    if (
      piece.token === 'call' &&
      piece.text === 'isset' &&
      pieces[at + 1]?.text === '(' &&
      pieces[at + 2]?.token === 'name' &&
      pieces[at + 3]?.text === ')'
    ) {
      issetGuarded.add(pieces[at + 2]!.text)
    }
  }

  return { names, issetGuarded }
}

/** Every name an expression reads — a bare variable or a called function. */
export const formulaNames = (code: string): Set<string> =>
  formulaReferences(code).names

/**
 * The same expression with one name rewritten, and nothing else touched.
 *
 * Only the pieces the scan calls a name: a word-boundary replace rewrites the
 * name inside `roomType == 'OLD'` too, which changes what the expression
 * compares against rather than what it reads.
 */
export function renameFormulaName(
  code: string,
  from: string,
  to: string,
): string {
  let out = ''
  for (const piece of scanFormula(code)) {
    const named = piece.token === 'name' || piece.token === 'call'
    out += named && piece.text === from ? to : piece.text
  }
  return out
}
